"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import { CardGrid } from "@/components/common/card-grid";
import { Button } from "@/components/ui/button";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export const AsideList = ({
  items,
  activeIndex = 0,
  className,
  withoutScroll = false,
}: {
  items: ReactNode[];
  activeIndex?: number;
  className?: string;
  withoutScroll?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showUpArrow, setShowUpArrow] = useState(false);
  const [showDownArrow, setShowDownArrow] = useState(true);

  const scrollUp = () => {
    if (!containerRef.current) return;

    const scrollAmount = containerRef.current.clientHeight * 0.8;
    containerRef.current.scrollBy({
      top: -scrollAmount,
      behavior: "smooth",
    });
  };

  const scrollDown = () => {
    if (!containerRef.current) return;

    const scrollAmount = containerRef.current.clientHeight * 0.8;
    containerRef.current.scrollBy({
      top: scrollAmount,
      behavior: "smooth",
    });
  };

  const checkScrollPosition = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    // Show up arrow only if we've scrolled down
    setShowUpArrow(scrollTop > 10);

    // Show down arrow only if we haven't reached the bottom
    setShowDownArrow(scrollTop < scrollHeight - clientHeight - 10);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      // Initial check
      checkScrollPosition();

      return () => {
        container.removeEventListener("scroll", checkScrollPosition);
      };
    }
  }, []);

  if (items.length <= 3) {
    return <CardGrid items={items} showSlider={false} />;
  }

  if (withoutScroll) {
    return (
      <div
        ref={containerRef}
        className="-mr-3 flex h-full flex-col gap-6 overflow-y-auto pr-1"
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="aside-list-item w-full flex-none snap-start"
          >
            {item}
          </div>
        ))}
      </div>
    );
  }

  const buttonStyle =
    "border-remedium-blue/10 absolute  left-1/2 z-100 -translate-x-1/2 rounded-full border bg-white shadow-xl transition-all duration-200 hover:scale-105 hover:bg-white";

  const buttonRingStyle =
    "bg-blue pointer-events-none absolute  left-[calc(50%-32px)] z-99 h-[64px] w-[64px] rounded-full backdrop-blur-xs";

  return (
    <>
      <div
        className={cn(
          "relative mx-auto hidden h-[600px] w-full max-w-md select-none xl:block",
          className,
        )}
      >
        {/*/!* Top gradient overlay *!/*/}
        {/*<div*/}
        {/*  className={`from-background pointer-events-none absolute top-0 right-0 left-0 z-10 h-24 bg-gradient-to-b to-transparent transition-opacity duration-300 ${*/}
        {/*    showUpArrow ? "opacity-100" : "opacity-0"*/}
        {/*  }`}*/}
        {/*/>*/}

        {/*/!* Bottom gradient overlay *!/*/}
        {/*<div*/}
        {/*  className={`from-background pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-24 bg-gradient-to-t to-transparent transition-opacity duration-300 ${*/}
        {/*    showDownArrow ? "opacity-100" : "opacity-0"*/}
        {/*  }`}*/}
        {/*/>*/}

        <Button
          variant="secondary"
          size="icon"
          onClick={scrollUp}
          aria-label="Scroll up"
          className={cn(
            buttonStyle,
            "top-[-20px]",
            showUpArrow ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          <ArrowUpIcon className="h-4 w-4" />
        </Button>

        <div
          className={cn(
            buttonRingStyle,
            `top-[-32px]`,
            showUpArrow ? "opacity-100" : "opacity-0",
          )}
        ></div>

        {/* Down Arrow */}
        <Button
          variant="secondary"
          size="icon"
          onClick={scrollDown}
          aria-label="Scroll down"
          className={cn(
            buttonStyle,
            "bottom-[-20px]",
            showDownArrow ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          <ArrowDownIcon className="h-4 w-4" />
        </Button>

        <div
          className={cn(
            buttonRingStyle,
            `bottom-[-32px]`,
            showDownArrow ? "opacity-100" : "opacity-0",
          )}
        ></div>

        <div
          ref={containerRef}
          className="aside-list-scrollbar -mr-3 flex h-full flex-col gap-6 overflow-y-auto pr-1"
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="aside-list-item w-full flex-none snap-start"
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <div className={"block xl:hidden"}>
        <CardGrid activeIndex={activeIndex} items={items} />
      </div>
    </>
  );
};
